/**
 * The wound clocks — shock, pain and blood (AoD §5), and the states they drive
 * (§6). This is where "no HP bar" stops being a slogan and becomes arithmetic.
 *
 * Three clocks, three different shapes, deliberately:
 *
 * - **Shock** is *instant and once* — it empties the dice you were about to
 *   fight with, and it comes back. It is why a blow that does not injure you
 *   much can still lose you the exchange.
 * - **Pain** is *persistent and grit-reduced* — recomputed from the wound list
 *   every time it is asked for, never accumulated, and it goes away only as
 *   wounds heal. It is why a broken arm is a bad fortnight, not a bad minute.
 * - **Blood** is *monotone and fatal* — the only clock that ends the character,
 *   and the only one nothing in the game undoes quickly. It is why first aid
 *   is the most time-critical act in Annwn.
 *
 * None of them is a pool of damage. You cannot spend shock to kill someone and
 * you cannot heal blood by resting for an hour, and no code anywhere adds a
 * wound level to anything.
 *
 * **What owns what.** A `Vitals` record is the natural owner of a body *and*
 * its wounds: `body.ts` holds part states, `wound.ts` holds records beside
 * them, and neither imports the other. This module is where the two finally
 * meet, which is also why the module graph stays acyclic — vitals imports
 * both, nothing imports vitals except the systems built on top of it.
 *
 * **Events, not narration.** Every threshold crossing returns a `ClockEvent`:
 * a type and some refs, no prose. M1-06 turns those into sentences and M2 lifts
 * them into `SimEvent`s with positions and audiences. A clock that wrote
 * English would be a clock the narration pass could not rewrite.
 */
import { clamp01, combatPool, deriveCapabilities, grit } from './body.js';
import { HUNGER_NOURISHMENT, HUNGER_PAIN } from './needs.js';
import { TICKS_PER_GAME_HOUR } from './time.js';
import { effectivePain, totalBleedRate } from './wound.js';
/* -------------------------------------------------------------------------- *
 * Clock events
 * -------------------------------------------------------------------------- */
/**
 * What the clocks report. Includes M1-05's infection vocabulary because
 * infection is a wound clock too and `infection.ts` imports this module — one
 * list here keeps the dependency pointing one way.
 *
 * Never renamed once archived: old rows in the event log spell them this way.
 */
export const CLOCK_EVENT_TYPES = [
    'staggered',
    'knocked-down',
    'blood-stage',
    'senseless',
    'roused',
    'died',
    'infected',
    'infection-stage',
    'infection-halted',
    'amputated',
    // M4-04's recovery. `mending` is a wound stepping down a level, `healed` is
    // one reaching the end and becoming a scar, `re-opened` is strain tearing one
    // back open.
    'mending',
    'healed',
    're-opened',
];
function clockEvent(type, ...refs) {
    return { type, refs };
}
/* -------------------------------------------------------------------------- *
 * Blood
 * -------------------------------------------------------------------------- */
/** A whole character's worth of blood (AoD §5.3). */
export const MAX_BLOOD = 100;
/**
 * Blood is stored in thousandths.
 *
 * Bleeding is authored per *game hour* but has to accrue per *sim tick*, or a
 * character bleeds in 1,500-tick steps and first aid becomes a game of beating
 * the hour boundary. Thousandths make that accrual exact integer arithmetic —
 * 6 blood/hour is exactly 4 milli/tick — for the same reason movement counts
 * milliticks: a float accumulator drifts, and a replay that diverges only on
 * its second run is hours to find and one line to cause.
 */
export const BLOOD_SCALE = 1000;
export const MAX_BLOOD_MILLI = MAX_BLOOD * BLOOD_SCALE;
/**
 * The blood ladder (AoD §5.3), in order. `hale` is anything above `pale`.
 * `dead` is a stage rather than a special case so that a crossing into it is
 * reported by the same edge detector as every other crossing.
 */
export const BLOOD_STAGES = ['hale', 'pale', 'failing', 'dying', 'dead'];
/** Blood at or below which each stage begins. Ported unchanged from AoD §5.3. */
export const BLOOD_THRESHOLDS = {
    /** Pale, and everything is three-quarters of what it was. */
    pale: 60,
    /** Pools halved; cannot fight. */
    failing: 40,
    /** Unconscious and dying, and the drain does not stop. */
    dying: 25,
};
export function bloodStage(blood) {
    if (blood <= 0)
        return 'dead';
    if (blood < BLOOD_THRESHOLDS.dying)
        return 'dying';
    if (blood < BLOOD_THRESHOLDS.failing)
        return 'failing';
    if (blood < BLOOD_THRESHOLDS.pale)
        return 'pale';
    return 'hale';
}
/** What each stage multiplies pools and work rate by. */
const BLOOD_FACTORS = {
    hale: 1,
    pale: 0.75,
    failing: 0.5,
    dying: 0,
    dead: 0,
};
export function bloodFactor(stage) {
    return BLOOD_FACTORS[stage];
}
/**
 * Blood restored per game day, well fed and rested (AoD §5.3). Scaled by
 * `nourishment` and cut by fever, which is what makes the Cook and the Meddyg
 * worth their keep — and what M3-06 and M4-04 plug into.
 */
export const BLOOD_REGEN_PER_DAY = 2;
/**
 * Wounds below this level close on their own. At or above it, somebody has to
 * close them.
 *
 * AoD §5.3 does not say — and M1-07's harness is what made the omission
 * visible: with nothing clotting, a fortnight-old graze bleeding 1 a game hour
 * killed every survivor of every fight in the batch. A scratch stops. A cut to
 * the bone does not, which is exactly why AoD calls first aid the most
 * time-critical act in the game and why the level-3 anchor is written about a
 * wound that keeps running.
 */
export const CLOTTING_LEVEL = 3;
/** Game hours a minor wound takes to stop bleeding by itself. */
export const CLOT_HOURS = 2;
/** Rate below which a closing wound is simply closed. */
const CLOT_FLOOR = 0.05;
/** Per-tick multiplier that takes a minor wound to ~1% over `CLOT_HOURS`. */
const CLOT_FACTOR = Math.exp(Math.log(0.01) / (CLOT_HOURS * 1500));
/* -------------------------------------------------------------------------- *
 * Shock and knockdown
 * -------------------------------------------------------------------------- */
/**
 * Shock at or above this multiple of a character's *full* pool knocks them out
 * rather than merely staggering them (AoD §6's "shock overflow knockout").
 *
 * Two tiers rather than one because AoD asks for both and gives a number for
 * neither: overflowing the dice you have left is common and costs you the
 * exchange; overflowing twice the dice you ever had is a blow that puts the
 * lights out. Tunable, and the only invented constant in the shock rule.
 */
export const SHOCK_KNOCKOUT_FACTOR = 2;
/** How long a shock knockout lasts before a rousing check — ten game minutes. */
export const KNOCKOUT_TICKS = 250;
/** A knockdown check succeeds on this or better; `knockdownMod` is added to it. */
export const KNOCKDOWN_TARGET = 10;
/** Sides on the knockdown die. AoD's "CON-derived roll", on Annwn's dice. */
export const KNOCKDOWN_DIE = 10;
/* -------------------------------------------------------------------------- *
 * The record
 * -------------------------------------------------------------------------- */
/**
 * `execution` is deliberately its own cause rather than a `wound` (M2-07). It
 * is the only one that required somebody to *decide*, and separating it is what
 * lets the archive answer "was I killed, or did I bleed out" — a question the
 * cauldron's biography (M4-07) and any galanas claim after it both turn on.
 */
export const DEATH_CAUSES = ['wound', 'blood', 'infection', 'execution'];
export function createVitals(body, options = {}) {
    const blood = options.blood ?? MAX_BLOOD;
    return {
        body,
        wounds: [],
        bloodMilli: Math.round(clampRange(blood, 0, MAX_BLOOD) * BLOOD_SCALE),
        bleedCarry: 0,
        pool: 0,
        staggered: false,
        prone: false,
        senselessUntil: 0,
        dead: false,
        deathCause: null,
        fever: 0,
        nourishment: clamp01(options.nourishment ?? 1),
        hunger: options.hunger ?? 'fed',
        relief: 0,
        // Nobody is born marked. What the cauldron writes here, it writes later.
        marks: [],
    };
}
/** Blood in whole units, 0–100. The form every rule and every test reads. */
export function bloodOf(vitals) {
    return vitals.bloodMilli / BLOOD_SCALE;
}
/**
 * Fields a `Vitals` has gained since the first save format, with the value a
 * fresh body starts at.
 *
 * Declared beside the migration rather than beside `createVitals`, for the
 * reason `COMBATANT_BACKFILL` is: this list is *only* about what old records
 * can lack. It is a record of history, and it should not grow every time a
 * field does — only when a field is added that a saved body might not have.
 */
const VITALS_BACKFILL = {
    bleedCarry: 0,
    pool: 0,
    staggered: false,
    prone: false,
    senselessUntil: 0,
    dead: false,
    deathCause: null,
    fever: 0,
    nourishment: 1,
    hunger: 'fed',
    relief: 0,
    ache: 0,
};
/**
 * Bring a body parsed off a character record (M3-07) up to the current shape.
 *
 * The same job `hydrateWorld` does for a snapshot, at the scale of one
 * character: the storage layer accepts fields it does not recognise so that a
 * newer save still loads after a rollback, and the price of that permissiveness
 * is that an *older* save arrives missing whatever it never had. A missing
 * number here is worse than a crash — `undefined + 1` is `NaN`, and `NaN` blood
 * belongs to no stage, so nothing ever resolves the character either way.
 *
 * Mutates and returns the body it was given: the caller has just parsed it out
 * of JSON and owns the only reference.
 */
export function hydrateVitals(vitals) {
    const record = vitals;
    for (const [key, value] of Object.entries(VITALS_BACKFILL)) {
        if (record[key] === undefined)
            record[key] = value;
    }
    // A fresh array rather than a shared one, for the reason `defences` gets one:
    // a single array handed to two bodies gives them each other's wounds. The
    // marks are the same trap with a shorter list (M4-07).
    if (!Array.isArray(record['marks']))
        record['marks'] = [];
    if (!Array.isArray(record['wounds']))
        record['wounds'] = [];
    else
        for (const wound of record['wounds'])
            hydrateWound(wound);
    return vitals;
}
/**
 * The M4-01 treatment fields a wound gained since the first save format.
 *
 * `dirty` defaults to `false` rather than guessing at the contamination of a
 * wound taken before the flag existed: the number that drives infection is
 * `infectionMod`, which the old record already carries, so a conservative
 * `false` costs the player nothing — it only means Clean has no *extra* work to
 * undo on a wound whose filth was never recorded.
 */
const WOUND_BACKFILL = {
    dirty: false,
    sutured: false,
    cauterized: false,
    dressedAt: 0,
    // M4-04. `0` on an old record is right for an open wound and *also* right for
    // one saved as `healed` before scars had a date: the provenance is what a scar
    // is read from, and "we do not know which day it closed" is an honest gap.
    healedAt: 0,
};
function hydrateWound(wound) {
    for (const [key, value] of Object.entries(WOUND_BACKFILL)) {
        if (wound[key] === undefined)
            wound[key] = value;
    }
}
/* -------------------------------------------------------------------------- *
 * Derived state
 * -------------------------------------------------------------------------- */
/**
 * The unification promised by AoD §6: head trauma, blood loss and shock
 * overflow all arrive at the same number, and everything downstream asks that
 * number rather than asking why.
 *
 * Note what is *not* in here: pain. Pain costs dice, not awareness — a man with
 * a shattered thigh is in agony and entirely awake, and that distinction is the
 * whole reason the two clocks are separate.
 */
export function consciousness(plan, vitals, tick) {
    if (vitals.dead)
        return 0;
    if (vitals.senselessUntil > tick)
        return 0;
    const stage = bloodStage(bloodOf(vitals));
    if (stage === 'dying' || stage === 'dead')
        return 0;
    const structural = deriveCapabilities(plan, vitals.body).consciousness;
    return clamp01(structural * bloodFactor(stage));
}
/** Down: out of the fight, helpless, and M2-07 decides what others may do. */
export function isDown(plan, vitals, tick) {
    return consciousness(plan, vitals, tick) <= 0;
}
/** Below `failing` a character cannot fight at all (AoD §5.3), down or not. */
export function canFight(plan, vitals, tick) {
    if (isDown(plan, vitals, tick))
        return false;
    return bloodOf(vitals) >= BLOOD_THRESHOLDS.failing;
}
/** How much of a pool fever takes. Fevered fighters are half the fighters. */
export const FEVER_POOL_PENALTY = 0.5;
/**
 * The dice a character actually brings to an exchange: their combat pool,
 * scaled by blood and fever, less their effective pain.
 *
 * Pain subtracts and blood multiplies, in that order, because that is what AoD
 * §5.2 and §5.3 each say about their own clock. Healthy, unwounded and unfed-up
 * it reduces to `combatPool` exactly, so nothing pays for the machinery until
 * something is wrong.
 */
export function effectivePool(plan, vitals, attributes) {
    if (vitals.dead)
        return 0;
    const capabilities = deriveCapabilities(plan, vitals.body);
    const base = combatPool(attributes, capabilities);
    const scaled = base * bloodFactor(bloodStage(bloodOf(vitals))) * (1 - FEVER_POOL_PENALTY * vitals.fever);
    // Hunger is charged as pain rather than as a second multiplier, so grit
    // reduces it — a tough man carries an empty stomach better (M3-06).
    // Wounds, hunger, and whatever the rain is doing to an old scar (M4-07).
    // All three are charged as *pain* so that grit carries them and a remedy
    // quiets them — a mark that bypassed the medicine system would be a mark
    // nobody could do anything about.
    const pain = feltPain(vitals, effectivePain(vitals.wounds, grit(attributes)) + hungerPain(vitals) + (vitals.ache ?? 0));
    return Math.max(0, Math.round(scaled) - Math.round(pain));
}
/**
 * Top the pool back up for a new exchange and clear the stagger it was spent
 * on. M2-02 calls this; M1 tests call it to set up a blow.
 */
export function refreshPool(plan, vitals, attributes) {
    vitals.pool = effectivePool(plan, vitals, attributes);
    vitals.staggered = false;
    return vitals.pool;
}
/* -------------------------------------------------------------------------- *
 * The clocks
 * -------------------------------------------------------------------------- */
/**
 * Kill a character. The single door — table `kill` effects, blood 0 and
 * infection completion all come through here, so "how did they die" has one
 * answer and one event.
 *
 * Idempotent: a corpse that takes another spear does not die twice, and a
 * replay that runs the tick again does not append a second death to the log.
 */
export function die(vitals, cause) {
    if (vitals.dead)
        return [];
    vitals.dead = true;
    vitals.deathCause = cause;
    vitals.prone = true;
    return [clockEvent('died', cause)];
}
/**
 * Apply shock to the pool (AoD §5.1).
 *
 * Overflowing what is left staggers; overflowing `SHOCK_KNOCKOUT_FACTOR` times
 * the full pool puts them out. The pool is allowed to go negative — the debt
 * carries into the exchange the stagger costs them, which is what makes two
 * quick blows worse than one big one at the same total.
 */
export function applyShock(plan, vitals, attributes, shock, tick) {
    if (vitals.dead || shock <= 0)
        return [];
    const events = [];
    const before = vitals.pool;
    vitals.pool = before - shock;
    if (shock > before && !vitals.staggered) {
        vitals.staggered = true;
        events.push(clockEvent('staggered'));
    }
    const full = effectivePool(plan, vitals, attributes);
    if (shock >= Math.max(1, full) * SHOCK_KNOCKOUT_FACTOR && vitals.senselessUntil <= tick) {
        vitals.senselessUntil = tick + KNOCKOUT_TICKS;
        events.push(clockEvent('senseless', 'shock'));
    }
    return events;
}
/**
 * The knockdown check a level 3+ wound owes (AoD §5.1). `knockdownMod` is the
 * table's, and it is negative for the blows that take your legs from under you.
 *
 * Returns true if they went down. Already prone costs no roll — the dice have
 * to stay in step across a replay, and rolling for a man on the ground would
 * shift every subsequent draw.
 */
export function checkKnockdown(rng, vitals, attributes, entry) {
    if (vitals.dead || vitals.prone)
        return [];
    const roll = rng.roll(KNOCKDOWN_DIE);
    const footing = Math.round(attributes.agility / 4);
    if (roll + footing + entry.knockdownMod >= KNOCKDOWN_TARGET)
        return [];
    vitals.prone = true;
    return [clockEvent('knocked-down')];
}
/**
 * Everything one landed blow does to the clocks: the table's `kill`, the shock,
 * the re-strike, and the knockdown check.
 *
 * **Re-striking a wounded part re-applies its shock** — AoD's nastiest rule,
 * kept. Aiming for a wound already there is a legitimate and vicious tactic,
 * and M2-03 hands it to players. The re-applied shock is the *worst* prior
 * wound on the part, not the sum of them: summing would let a dozen scratches
 * out-stagger a broken femur, which is the hit-point thinking this whole model
 * exists to avoid.
 *
 * Roll order is fixed: shock (no roll) before the knockdown check (one roll).
 * A roll inserted before that check shifts every subsequent draw of the fight.
 */
export function sufferBlow(rng, plan, vitals, attributes, result, tick) {
    if (vitals.dead)
        return [];
    const events = [];
    // The wound the blow just wrote is already on the list, so the re-strike
    // search has to exclude it — a wound cannot re-strike itself.
    const inflicted = result.application?.wound ?? null;
    const restrike = worstPriorShock(vitals.wounds, result.part.id, inflicted);
    const shock = result.entry.shock + restrike;
    events.push(...applyShock(plan, vitals, attributes, shock, tick));
    if (result.application?.knockdownCheck) {
        events.push(...checkKnockdown(rng, vitals, attributes, result.entry));
    }
    if (result.application?.fatal) {
        events.push(...die(vitals, 'wound'));
    }
    return events;
}
function worstPriorShock(wounds, part, exclude) {
    let worst = 0;
    for (const wound of wounds) {
        if (wound === exclude)
            continue;
        if (wound.part !== part)
            continue;
        if (wound.state === 'healed')
            continue;
        if (wound.shock > worst)
            worst = wound.shock;
    }
    return worst;
}
/**
 * One sim tick of bleeding, and the rousing check a shock knockout owes.
 *
 * Registered on the `sim` phase rather than `hour` because a wound that only
 * bled on the hour boundary would make first aid a race against a clock the
 * player cannot see. The rate is authored per hour and accrues per tick;
 * `bleedCarry` is what makes that exact.
 *
 * Threshold crossings emit exactly one event each, in order, even when a single
 * tick falls through two of them — which a severing wound at 18/hour can do.
 */
export function advanceClocks(vitals, tick) {
    if (vitals.dead)
        return [];
    const events = [];
    if (vitals.senselessUntil !== 0 && vitals.senselessUntil <= tick) {
        vitals.senselessUntil = 0;
        // Rousing from shock says nothing about the blood: a man who comes round
        // at 20 blood is awake for as long as it takes him to notice.
        events.push(clockEvent('roused'));
    }
    const before = bloodStage(bloodOf(vitals));
    clot(vitals);
    vitals.bleedCarry += Math.round(totalBleedRate(vitals.wounds) * BLOOD_SCALE);
    const drop = Math.floor(vitals.bleedCarry / TICKS_PER_GAME_HOUR);
    if (drop > 0) {
        vitals.bleedCarry -= drop * TICKS_PER_GAME_HOUR;
        vitals.bloodMilli = Math.max(0, vitals.bloodMilli - drop);
    }
    events.push(...crossings(before, bloodStage(bloodOf(vitals))));
    if (vitals.bloodMilli <= 0)
        events.push(...die(vitals, 'blood'));
    return events;
}
/**
 * Minor wounds close over. Serious ones, and anything festering, do not.
 *
 * Multiplicative rather than a countdown so no wound needs a new field to
 * remember how long it has been open — the rate *is* the memory, and a wound
 * that has been bleeding an hour is already most of the way shut.
 */
function clot(vitals) {
    for (const wound of vitals.wounds) {
        if (wound.bleedRate <= 0)
            continue;
        if (wound.level >= CLOTTING_LEVEL)
            continue;
        if (wound.state === 'infected')
            continue;
        const closing = wound.bleedRate * CLOT_FACTOR;
        wound.bleedRate = closing < CLOT_FLOOR ? 0 : closing;
    }
}
/** Run the sim-phase clocks over a span of ticks. The harness's convenience. */
export function advanceClocksOver(vitals, firstTick, ticks) {
    const events = [];
    for (let i = 0; i < ticks; i += 1) {
        events.push(...advanceClocks(vitals, firstTick + i));
    }
    return events;
}
/**
 * Pain a body is actually feeling: what it is carrying, less what it has taken
 * for it (M4-03).
 *
 * Floored at zero and applied *after* grit, deliberately. Willow bark does not
 * make a man tougher than he is — it takes the edge off what is already getting
 * through — and a relief applied before grit would have let a draught turn a
 * hurt character into a stronger one than an unhurt one.
 */
export function feltPain(vitals, pain) {
    return Math.max(0, pain - Math.max(0, vitals.relief ?? 0));
}
/** Dice hunger costs, before grit. `undefined` on an old save reads as fed. */
export function hungerPain(vitals) {
    return HUNGER_PAIN[vitals.hunger] ?? 0;
}
/** What this body's hunger says blood regeneration is worth (M3-06). */
export function nourishmentFor(band) {
    return HUNGER_NOURISHMENT[band] ?? 1;
}
/**
 * The `day` phase: blood comes back, slowly, if you are fed, rested and not
 * fevered.
 *
 * Two per day against a level-3 cut's 144 is not a race anybody wins by
 * resting — which is the point. Recovery is for after the bleeding stopped.
 *
 * `rest` is M3-06's fourth factor and the only one that can be *zero*: a
 * `REST_RECOVERY` of 0 for `exposed` is what makes "hungry, on wet ground,
 * regenerates nothing" true rather than merely slow. It defaults to 1 so that
 * every caller written before there was anywhere to sleep keeps its meaning.
 */
export function advanceDailyRecovery(vitals, rest = 1) {
    if (vitals.dead)
        return [];
    const rate = BLOOD_REGEN_PER_DAY * vitals.nourishment * (1 - vitals.fever) * Math.max(0, rest);
    if (rate <= 0)
        return [];
    const before = bloodStage(bloodOf(vitals));
    vitals.bloodMilli = Math.min(MAX_BLOOD_MILLI, vitals.bloodMilli + Math.round(rate * BLOOD_SCALE));
    return crossings(before, bloodStage(bloodOf(vitals)));
}
/**
 * One `blood-stage` event per threshold actually crossed, in the order they
 * were crossed — upward as they worsen, downward as they mend.
 */
function crossings(before, after) {
    const from = BLOOD_STAGES.indexOf(before);
    const to = BLOOD_STAGES.indexOf(after);
    if (from === to)
        return [];
    const events = [];
    const step = to > from ? 1 : -1;
    for (let i = from + step; i !== to + step; i += step) {
        events.push(clockEvent('blood-stage', BLOOD_STAGES[i]));
    }
    return events;
}
function clampRange(value, min, max) {
    if (!Number.isFinite(value))
        return min;
    return Math.max(min, Math.min(max, value));
}
//# sourceMappingURL=vitals.js.map